import { useEffect, useState } from 'react';
import { elevate, localStorageBackedSession } from '@apptus/esales-api';
import { getClusterId, getMarket, getLocale, getTouchpoint, getViewId, getVoyadoSession, hasCookieConsent } from '../context/search/services/Voyado/helpers/voyadoHelpers';
import { elevateFetchShim } from '../context/search/services/Voyado/helpers/elevateFetchShim';
import mapProduct from '../context/search/services/Voyado/helpers/mappers/mapProduct';
import fetchContextualPrices from '../context/search/services/Voyado/helpers/fetchContextualPrices';
import { ISearchAndFilterProduct } from '../context/search/types';

interface RecommendationConfig {
  productKey?: string;
  context?: string;
  pageReference?: string;
  presetRecommendationList?: string;
  recommendationList?: string;
  recommendationType?: string;
  sectionId: string;
}

interface RecommendationResult {
  products: ISearchAndFilterProduct[];
  error: string | null;
  isLoading: boolean;
}

const recommendationsCache = new Map<string, RecommendationResult>();
const pendingRequests = new Map<string, Promise<Map<string, any>>>();
const sectionConfigs = new Map<string, RecommendationConfig>();
let collectionTriggered = false;

const generateSectionId = (productKey: string, recommendationList: string | undefined, presetRecommendationList: string | undefined): string => {
  if (recommendationList) {
    try {
      const parsed = JSON.parse(recommendationList);
      const firstId = parsed.recommendationLists?.[0]?.id || parsed.recommendationLists?.[0]?.algorithm;
      return `section-${productKey || 'unknown'}-${firstId || 'default'}`;
    } catch {
      return `section-${productKey || 'unknown'}-${presetRecommendationList || 'default'}`;
    }
  }
  return `section-${productKey || 'unknown'}-${presetRecommendationList || 'default'}`;
};

// Collect all productPage sections from DOM so we can combine into one call to Voyado instead of one per carousel
const collectProductPageSections = (): RecommendationConfig[] => {
  const sections: RecommendationConfig[] = [];
  const nodes = document.querySelectorAll('[data-app-layer-portal="voyado-recommendations"]');

  nodes.forEach((node) => {
    if (!(node instanceof HTMLElement)) return; // Safety check to prevent null dataset access
    const dataset = node.dataset;

    if (dataset.productKey && !dataset.context) {
      const sectionId = generateSectionId(
        dataset.productKey,
        dataset.recommendationList,
        dataset.presetRecommendationList
      );

      sections.push({
        productKey: dataset.productKey,
        context: dataset.context,
        pageReference: dataset.pageReference,
        presetRecommendationList: dataset.presetRecommendationList,
        recommendationList: dataset.recommendationList,
        recommendationType: dataset.recommendationType,
        sectionId
      });
    }
  });

  return sections;
};
const fetchCombinedProductPageRecommendations = async (
  productKey: string,
  sections: RecommendationConfig[]
): Promise<Map<string, any>> => {
  try {
    const api = elevate({
      clusterId: getClusterId(),
      market: getMarket(),
      locale: getLocale(),
      touchpoint: getTouchpoint(),
      session: getVoyadoSession(localStorageBackedSession)
    });

    elevateFetchShim();
    const hasConsent = hasCookieConsent();
    const allRecLists: any[] = [];
    const sectionToRecListMap = new Map<string, string>(); // sectionId -> recList identifier

    sections.forEach((section) => {
      if (section.recommendationList) {
        try {
          const parsed = JSON.parse(section.recommendationList);
          if (parsed.recommendationLists && Array.isArray(parsed.recommendationLists)) {
            parsed.recommendationLists.forEach((recList: any) => {
              const recId = recList.id || recList.algorithm;
              if (!allRecLists.find(r => (r.id || r.algorithm) === recId)) {
                allRecLists.push(recList);
              }
              sectionToRecListMap.set(section.sectionId, recId);
            });
          }
        } catch (e) {
          console.warn('[VoyadoRecs] Failed to parse recommendationList', e);
        }
      } else if (section.presetRecommendationList) {
        sectionToRecListMap.set(section.sectionId, section.presetRecommendationList);
      }
    });

    if (allRecLists.length === 0) {
      throw new Error('No recommendation lists to fetch');
    }

    const productParams: any = {
      viewId: getViewId(),
      productKey
    };

    if (!hasConsent) {
      productParams.notify = false;
    }

    console.info('[VoyadoRecs] Making combined API call for', allRecLists.length, 'recommendation lists');

    const res = await api.query.productPage(
      productParams,
      { recommendationLists: allRecLists }
    );

    if (!res) {
      console.error('[VoyadoRecs] Combined API call returned empty response');
    }

    const results = new Map<string, any>();

    sections.forEach((section) => {
      const recId = sectionToRecListMap.get(section.sectionId);

      if (recId) {
        const matchedList = res.recommendationLists?.find(
          (list: any) => list.id === recId || list.algorithm === recId
        );
        if (matchedList) {
          results.set(section.sectionId, matchedList);
        }
      }
    });

    return results;
  } catch (err) {
    console.error('[VoyadoRecs] fetchCombinedProductPageRecommendations error:', err);
    throw err;
  }
};

export const useVoyadoRecommendationsCollector = (
  config: RecommendationConfig
): RecommendationResult => {
  const [result, setResult] = useState<RecommendationResult>({
    products: [],
    error: null,
    isLoading: true
  });

  useEffect((): (() => void) | void => {
    sectionConfigs.set(config.sectionId, config);

    if (!config.productKey || config.productKey === '' || config.context === 'minicart-adjacent') {
      setResult({ products: [], error: null, isLoading: false });
      return;
    }

    const cached = recommendationsCache.get(config.sectionId);
    if (cached) {
      console.info('[VoyadoRecs] Using cached results for section', config.sectionId);
      setResult(cached);
      return;
    }

    console.info('[VoyadoRecs] No cache found for section', config.sectionId, 'productKey:', config.productKey);

    if (collectionTriggered) {
      const existingRequest = pendingRequests.get(config.productKey);
      if (existingRequest) {
        existingRequest.then((results) => {
          const sectionResult = results.get(config.sectionId);
          if (sectionResult) {
            // Fetch contextual prices if not on GB site
            const productKeys = (sectionResult.productGroups || []).map((group: any) => group.products[0].key);
            let contextualPrices = {};

            const sfApiConfigStr = document.querySelector('[data-storefront-api-config]')?.textContent;
            let shouldFetchContextual = false;

            if (sfApiConfigStr) {
              try {
                const parsedConfig = JSON.parse(sfApiConfigStr);
                if (parsedConfig.country && parsedConfig.country !== 'GB') {
                  shouldFetchContextual = true;
                }
              } catch (e) {
                // failed to parse config
              }
            }

            Promise.resolve().then(async () => {
              if (shouldFetchContextual && productKeys.length > 0) {
                contextualPrices = await fetchContextualPrices(productKeys);
              }

              const products = (sectionResult.productGroups || [])
                .map((group: any, index: number) => {
                  if (group.products?.[0]?.link) {
                    return mapProduct(
                      group.products[0],
                      {
                        index,
                        pageIndex: 0,
                        pageMeta: 'recommendations',
                        recommendationType: config.recommendationType,
                        contextualPriceData: contextualPrices[group.products[0].key]
                      }
                    );
                  }
                  return null;
                })
                .filter(Boolean) as ISearchAndFilterProduct[];

              const cachedResult: RecommendationResult = {
                products,
                error: null,
                isLoading: false
              };
              recommendationsCache.set(config.sectionId, cachedResult);
              setResult(cachedResult);
            });
          }
        }).catch((err) => {
          setResult({
            products: [],
            error: err.message || 'Failed to fetch recommendations',
            isLoading: false
          });
        });
      }
      return;
    }

    collectionTriggered = true;
    const allSections = collectProductPageSections();

    const sectionsByProductKey = new Map<string, RecommendationConfig[]>();
    allSections.forEach((section) => {
      if (section.productKey) {
        if (!sectionsByProductKey.has(section.productKey)) {
          sectionsByProductKey.set(section.productKey, []);
        }
        const productKeySections = sectionsByProductKey.get(section.productKey);
        if (productKeySections) {
          productKeySections.push(section);
        }
      }
    });

    sectionsByProductKey.forEach((sections, productKey) => {
      const requestPromise = fetchCombinedProductPageRecommendations(productKey, sections);
      pendingRequests.set(productKey, requestPromise);

      requestPromise
        .then((results) => {
          pendingRequests.delete(productKey);

          results.forEach((result, sectionId) => {
            const sectionConfig = sectionConfigs.get(sectionId);
            if (!sectionConfig) {
              console.warn('[VoyadoRecs] No section config found for', sectionId);
              return;
            }

            // Fetch contextual prices if not on GB site
            const productKeys = (result.productGroups || []).map((group: any) => group.products[0].key);
            let contextualPrices = {};

            const sfApiConfigStr = document.querySelector('[data-storefront-api-config]')?.textContent;
            let shouldFetchContextual = false;

            if (sfApiConfigStr) {
              try {
                const parsedConfig = JSON.parse(sfApiConfigStr);
                if (parsedConfig.country && parsedConfig.country !== 'GB') {
                  shouldFetchContextual = true;
                }
              } catch (e) {
                // failed to parse config
              }
            }

            Promise.resolve().then(async () => {
              if (shouldFetchContextual && productKeys.length > 0) {
                contextualPrices = await fetchContextualPrices(productKeys);
              }

              const products = (result.productGroups || [])
                .map((group: any, index: number) => {
                  if (group.products?.[0]?.link) {
                    return mapProduct(
                      group.products[0],
                      {
                        index,
                        pageIndex: 0,
                        pageMeta: 'recommendations',
                        recommendationType: sectionConfig.recommendationType,
                        contextualPriceData: contextualPrices[group.products[0].key]
                      }
                    );
                  }
                  return null;
                })
                .filter(Boolean) as ISearchAndFilterProduct[];

              console.info('[VoyadoRecs] Caching results for section', sectionId, 'with', products.length, 'products');

              const cachedResult: RecommendationResult = {
                products,
                error: null,
                isLoading: false
              };

              recommendationsCache.set(sectionId, cachedResult);
            });
          });
        })
        .catch((err) => {
          pendingRequests.delete(productKey);

          const errorResult: RecommendationResult = {
            products: [],
            error: err.message || 'Failed to fetch recommendations',
            isLoading: false
          };

          sections.forEach((section) => {
            recommendationsCache.set(section.sectionId, errorResult);
          });
        });
    });

    const checkCache = () => {
      const cached = recommendationsCache.get(config.sectionId);
      if (cached) {
        console.info('[VoyadoRecs] Found cached results for section', config.sectionId, 'with', cached.products.length, 'products');
        setResult(cached);
        return true;
      }
      return false;
    };

    if (!checkCache()) {
      console.info('[VoyadoRecs] Waiting for results for section', config.sectionId);
      const checkInterval = setInterval(() => {
        if (checkCache()) {
          clearInterval(checkInterval);
        }
      }, 100);

      setTimeout(() => clearInterval(checkInterval), 10000);

      return () => clearInterval(checkInterval);
    }

    // All other paths don't need cleanup
    return;
  }, [config.sectionId, config.productKey, config.recommendationList, config.presetRecommendationList, hasCookieConsent()]);

  return result;
};
